import { useEffect, useState } from "react";
import { Star, Plus, Trash2, Loader2, Quote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const inputCls = "bg-[#050505] border-white/10 rounded-none h-11 text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0";

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= n ? "fill-[#FFB800] text-[#FFB800]" : "text-zinc-700"}`} />
    ))}
  </span>
);

export function ReviewsView() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nw, setNw] = useState({ name: "", rating: 5, vehicle: "", text: "" });

  const load = async () => {
    try { const { data } = await api.get("/reviews"); setReviews(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nw.name.trim() || !nw.text.trim()) { toast.error(t("admin.rev_required")); return; }
    setSaving(true);
    try {
      await api.post("/reviews", nw);
      toast.success(t("admin.rev_added"));
      setNw({ name: "", rating: 5, vehicle: "", text: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("admin.rev_delete_confirm"))) return;
    try { await api.delete(`/reviews/${id}`); toast.success(t("admin.rev_deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-4xl" data-testid="reviews-view">
      <h3 className="font-display font-700 text-white text-lg tracking-tight mb-5 flex items-center gap-2">
        <Star className="w-4 h-4 text-[#FFB800]" /> {t("admin.rev_title")}
      </h3>

      <div className="border border-white/10 bg-[#0C0C0E] p-6 mb-8">
        <h4 className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#00E5FF] mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> {t("admin.rev_add")}</h4>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.rev_name")}</label>
            <Input value={nw.name} onChange={(e) => setNw((p) => ({ ...p, name: e.target.value }))} data-testid="review-add-name" className={inputCls} />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.rev_rating")}</label>
            <Select value={String(nw.rating)} onValueChange={(v) => setNw((p) => ({ ...p, rating: parseInt(v) }))}>
              <SelectTrigger data-testid="review-add-rating" className="bg-[#050505] border-white/10 rounded-none h-11 text-white focus:ring-0"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0C0C0E] border-white/10 text-white rounded-none">
                {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)} className="focus:bg-white/10 rounded-none">{n} ★</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.rev_vehicle")}</label>
            <Input value={nw.vehicle} onChange={(e) => setNw((p) => ({ ...p, vehicle: e.target.value }))} data-testid="review-add-vehicle" placeholder="BMW Série 1" className={inputCls} />
          </div>
          <div className="sm:col-span-3">
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">{t("admin.rev_text")}</label>
            <Textarea value={nw.text} onChange={(e) => setNw((p) => ({ ...p, text: e.target.value }))} data-testid="review-add-text" className="bg-[#050505] border-white/10 rounded-none text-white placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[70px]" />
          </div>
        </div>
        <button onClick={add} disabled={saving} data-testid="review-add-submit" className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {t("admin.rev_add")}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : reviews.length === 0 ? (
        <div className="border border-white/10 bg-[#0C0C0E] py-16 text-center text-zinc-500 font-mono-tech text-sm" data-testid="reviews-empty">{t("admin.rev_empty")}</div>
      ) : (
        <div className="space-y-px bg-white/10 border border-white/10">
          {reviews.map((r) => (
            <div key={r.id} className="bg-[#0C0C0E] p-5 flex items-start gap-4" data-testid={`review-row-${r.id}`}>
              <Quote className="w-5 h-5 text-[#E60000]/60 shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display font-700 text-white text-sm">{r.name}</span>
                  <Stars n={r.rating} />
                  {r.vehicle && <span className="font-mono-tech text-[11px] uppercase tracking-wider text-[#00E5FF]">{r.vehicle}</span>}
                </div>
                <p className="text-zinc-400 text-sm mt-1.5 leading-relaxed">{r.text}</p>
              </div>
              <button onClick={() => del(r.id)} data-testid={`review-delete-${r.id}`} className="w-9 h-9 shrink-0 flex items-center justify-center border border-white/10 text-zinc-500 hover:border-[#E60000] hover:text-[#E60000] transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
